import type { PaymentMethodConfig, ReceiptTemplateOverride, TipConfig } from '@kynetik/shared';

/**
 * Operating hours for a single day
 */
export interface DayHours {
  open: string; // 24-hour format "09:00"
  close: string; // 24-hour format "17:00"
}

/**
 * Location from API - merged BC data + POS settings
 */
export interface Location {
  // --- BigCommerce-managed (read-only) ---
  id: number;
  code: string;
  label: string;
  description?: string;
  address?: {
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    countryCode?: string;
  };
  phone?: string;
  email?: string;
  enabled: boolean;
  storefront_visibility?: boolean;
  type_id?: string;
  time_zone?: string;
  operating_hours?: {
    sunday?: DayHours | null;
    monday?: DayHours | null;
    tuesday?: DayHours | null;
    wednesday?: DayHours | null;
    thursday?: DayHours | null;
    friday?: DayHours | null;
    saturday?: DayHours | null;
  };
  latitude?: number;
  longitude?: number;
  created_at?: string;
  updated_at?: string;

  // Link to edit in BigCommerce admin
  bcManageUrl?: string;

  // --- Kynetik POS settings (editable) ---
  posSettings: {
    locationId: number;
    locationCode?: string;
    bcLabel?: string; // Original BC label (for reference)
    bcTimezone?: string; // Original BC timezone
    displayName: string;
    timezone?: string;
    currency: string;
    taxZoneId?: number;
    bcChannelId?: number; // Override store channel for this location
    pickupMethodId?: number; // BC pickup method for BOPIS tax calculation
    devices: string[];
    defaultReceiptMethod: ReceiptMethod;
    requireCustomerEmail: boolean;
    requireCustomerPhone: boolean;
    paymentMethods: PaymentMethodConfig;
    tip: TipConfig;
    // Receipt template overrides (optional, inherits from merchant defaults if not set)
    receiptOverride?: ReceiptTemplateOverride;
  } | null;
  isConfiguredForPOS: boolean;
}

export interface Currency {
  code: string;
  name: string;
  symbol: string;
  isDefault: boolean;
}

export interface TaxZone {
  id: number;
  name: string;
  enabled: boolean;
  shopper_target_settings: {
    locations: Array<{
      country_code: string;
      subdivision_codes?: string[];
    }>;
  };
}

export type ReceiptMethod = 'print' | 'email' | 'sms' | 'ask' | 'none';

export interface Channel {
  id: number;
  name: string;
  type: string;
  platform: string;
  status: string;
  isEnabled: boolean;
}

export interface PickupMethod {
  id: number;
  displayName: string;
  locationId: number;
  collectionInstructions?: string;
  collectionTimeDescription?: string;
}

export interface LocationFormData {
  displayName: string;
  currency: string;
  taxZoneId: number | null;
  bcChannelId: number | null; // null = use store default channel
  pickupMethodId: number | null; // Required for BOPIS tax calculation
  defaultReceiptMethod: ReceiptMethod;
  requireCustomerEmail: boolean;
  requireCustomerPhone: boolean;
  paymentMethods: PaymentMethodConfig;
  tip: TipConfig;
  // Receipt template overrides (optional, inherits from merchant defaults if not set)
  receiptOverride?: ReceiptTemplateOverride;
}

export const DEFAULT_PAYMENT_METHODS: PaymentMethodConfig = {
  cash: true,
  card: false,
  manualCard: false,
  giftCard: false,
  storeCredit: false,
};

export const DEFAULT_TIP_CONFIG: TipConfig = {
  enabled: false,
  options: [10, 15, 20],
};

export const RECEIPT_METHOD_OPTIONS = [
  { value: 'ask', content: 'Ask customer' },
  { value: 'email', content: 'Email' },
  { value: 'sms', content: 'SMS' },
  { value: 'print', content: 'Print' },
  { value: 'none', content: 'No receipt' },
] as const;
